const MIME_TAIL_MIN_BYTES = 100;

/**
 * Sync often stores `text/html` **plus** a second segment (raw multipart, plaintext alternative,
 * or another full HTML doc). The first segment renders in prose; the tail appears as an ugly
 * second block in the same message view — truncate when we can identify it.
 */
export function truncateAfterFirstClosingHtmlDocument(html: string): string {
  const lower = html.toLowerCase();
  const tag = "</html>";
  const idx = lower.indexOf(tag);
  if (idx === -1) {
    return html;
  }
  const end = idx + tag.length;
  const remainder = html.slice(end).trimStart();
  if (remainder.length === 0) {
    return html.slice(0, end).trimEnd();
  }
  const looksLikeMimeOrDup =
    /^--[A-Za-z0-9=_+-]{8,}/m.test(remainder) ||
    /^Content-Type:/im.test(remainder) ||
    /^MIME-Version:/im.test(remainder);
  if (remainder.length < MIME_TAIL_MIN_BYTES && !looksLikeMimeOrDup) {
    return html;
  }
  return html.slice(0, end).trimEnd();
}

/**
 * When there is no `<html>` wrapper but there is a `<body>…</body>`, drop a large junk tail
 * (second HTML document, MIME headers, or raw CSS) after the first `</body>`.
 */
export function truncateAfterFirstClosingBodyWhenJunkFollows(html: string): string {
  if (/<html\b/i.test(html)) {
    return html;
  }
  const lower = html.toLowerCase();
  const openIdx = lower.indexOf("<body");
  if (openIdx === -1) {
    return html;
  }
  const closeIdx = lower.indexOf("</body>", openIdx);
  if (closeIdx === -1) {
    return html;
  }
  const end = closeIdx + "</body>".length;
  const remainder = html.slice(end).trimStart();
  if (remainder.length < MIME_TAIL_MIN_BYTES) {
    return html;
  }
  const junkFollows =
    /<html\b/i.test(remainder) ||
    /<body\b/i.test(remainder) ||
    /^Content-Type:/im.test(remainder) ||
    /^MIME-Version:/im.test(remainder) ||
    /^--[A-Za-z0-9=_+-]{12,}/m.test(remainder) ||
    /^\/\*/.test(remainder);
  return junkFollows ? html.slice(0, end).trimEnd() : html;
}

/** Cut before the first RFC 2046 boundary line after substantial content (another MIME part). */
export function truncateAtFirstMimeMultipartBoundary(html: string): string {
  const re = /\r?\n--[A-Za-z0-9=_+-]{14,}(?:\r?\n|$)/g;
  const MIN_BEFORE = 280;
  let m: RegExpExecArray | null;
  while ((m = re.exec(html)) !== null) {
    if (m.index < MIN_BEFORE) {
      continue;
    }
    const rest = html.slice(m.index + m[0].length).trimStart();
    if (
      /^Content-Type:/im.test(rest) ||
      /^Content-Transfer-Encoding:/im.test(rest) ||
      rest.startsWith("--")
    ) {
      return html.slice(0, m.index).trimEnd();
    }
  }
  return html;
}

export function truncateTrailingAlternateMimeParts(html: string): string {
  let s = truncateAfterFirstClosingHtmlDocument(html);
  s = truncateAfterFirstClosingBodyWhenJunkFollows(s);
  s = truncateAtFirstMimeMultipartBoundary(s);
  return s;
}
